/**
 * The request parameters a screen's list is loaded with, answered from the
 * stored context: the screen's own filter codes, then those of the screens its
 * screen filters lead to. Every other request for rows of the same screen (one
 * record of it) sends the same, or it would not get the row the list shows.
 */
export const screenRequestFilters = (screen: any, getFilter: (code: string) => any): Record<string, any> => {
    const resolved: Record<string, any> = {};

    (screen?.filters ?? []).forEach((filter: any) => {
        const value = getFilter(filter.code);
        if (value) resolved[filter.code] = value;
    });

    const throughScreenFilters = (current: any, depth = 0) => {
        if (depth > 10 || !current?.screenFilters?.length) return;
        current.screenFilters.forEach((screenFilter: any) => {
            const related = current.columns?.find((column: any) => column.id === screenFilter.screenColumnId)?.relatedScreen;
            if (!related) return;
            const codes = [...(related.filters ?? []).map((filter: any) => filter.code), ...(related.queryParameters ?? [])];
            codes.forEach((code: string) => {
                const value = getFilter(code);
                if (value && !resolved[code]) resolved[code] = value;
            });
            throughScreenFilters(related, depth + 1);
        });
    };
    throughScreenFilters(screen);

    return resolved;
};

/**
 * The request parameters of a one-to-many table inside a record's form. Its filter codes are answered from the
 * stored context, the record's id being the fallback — except the codes that are the link to this very record (the
 * child's column pointing at it, the context code of the screen the record belongs to): those always carry the
 * record's id, or a context left over from another record lists that record's rows.
 */
export const subTableFilters = (
    childScreen: any,
    recordId: string | number,
    getFilter: (code: string) => any,
    ownCodes: Array<string | null | undefined>,
): Record<string, any> => {
    const own = new Set(ownCodes.filter(Boolean));
    const resolved: Record<string, any> = {};

    (childScreen?.filters ?? []).forEach((filter: any) => {
        resolved[filter.code] = own.has(filter.code) ? String(recordId) : (getFilter(filter.code) ?? String(recordId));
    });

    return resolved;
};

/**
 * The codes of a record's one-to-many table that are its link to the record: the child's column pointing at it, the
 * context code of the record's screen, and the parameters the API names on the record's column (a PARAMETER filter of
 * the child screen on that same pointing column — „Položky“ of a request read header_id = request_id).
 */
export const recordLinkCodes = (recordScreen: any, field: any): Array<string | null | undefined> => [
    field.targetColumn?.columnName,
    recordScreen?.contextCode,
    ...(recordScreen?.columns?.find((column: any) => column.id === field.screenColumnId)?.parentContextCodes ?? []),
];

/**
 * Saves a row's context filters exactly like clicking the row in a screen:
 * screen.contextCode -> row id, plus every column contextCode -> row value
 * (many-to-one {id, value} objects collapse to id). Empty row values clear
 * the corresponding context so no stale state leaks from a previous row.
 *
 * Screen data rows are keyed by screen column codes; a form record is keyed by
 * the table's column names instead, so it carries only the columns whose code
 * happens to match. Columns missing from the row are skipped rather than
 * cleared.
 */
export const applyRowContextFilters = (
    screen: any,
    row: any,
    setFilter: (code: string, value: string) => void,
    removeFilter: (code: string) => void
): void => {
    if (!screen || !row) return;

    if (screen.contextCode && row.id) {
        const idValue = typeof row.id === 'object' && row.id !== null && row.id.id !== undefined
            ? String(row.id.id)
            : String(row.id);
        setFilter(screen.contextCode, idValue);
    }

    screen.columns?.forEach((col: any) => {
        if (!col.contextCode) return;
        // A form record is keyed by the table's column names, so a screen column
        // whose code differs is simply absent from it. That says nothing about
        // the record's value, so leave the stored context alone - only a value
        // the row actually carries may clear it.
        if (!Object.prototype.hasOwnProperty.call(row, col.code)) return;
        const value = row[col.code];
        if (value === undefined || value === null) {
            removeFilter(col.contextCode);
            return;
        }
        const rawId = typeof value === 'object' && value !== null && value.id !== undefined
            ? value.id
            : value;
        if (rawId === undefined || rawId === null || rawId === '') {
            removeFilter(col.contextCode);
            return;
        }
        setFilter(col.contextCode, String(rawId));
    });
};
